import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { HelpAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import { PageHeader, Section } from '../components/forms';
import { Button, ErrorNote } from '../components/ui';
import { api, ApiError, type Salon } from '../lib/api';
import { useLocale, useT, type TKey } from '../lib/i18n';

type Privacy = {
  dpa: { currentVersion: string; acceptedVersion: string | null; acceptedAt: string | null; upToDate: boolean };
  supportAccessUntil: string | null;
  supportSessions: Array<{ name: string; until: string }>;
  supportLog: Array<{ action: string; at: string; by: string | null }>;
};

const LOGGED = ['support.allowed', 'support.revoked', 'support.session_started', 'support.session_ended', 'admin.trial_extended', 'admin.salon_created'];

/** Salon → Privacy en gegevens: processing agreement, support access, download everything. */
export function PrivacyPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const key = ['privacy', orgId];
  const privacy = useQuery({ queryKey: key, queryFn: () => api<Privacy>(`/orgs/${orgId}/privacy`) });
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const owner = salon.data?.role === 'owner';
  const onSuccess = (d: Privacy) => qc.setQueryData(key, d);
  const accept = useMutation({ mutationFn: () => api<Privacy>(`/orgs/${orgId}/privacy/dpa`, { method: 'POST' }), onSuccess });
  const support = useMutation({ mutationFn: (days: number) => api<Privacy>(`/orgs/${orgId}/privacy/support`, { method: 'PUT', body: JSON.stringify({ days }) }), onSuccess });

  const tag = locale === 'nl' ? 'nl-NL' : 'en-GB';
  const date = (iso: string) => new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
  const dateTime = (iso: string) => new Intl.DateTimeFormat(tag, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  const err = (e: unknown) => (e instanceof ApiError ? e.message : e ? t('error.generic') : null);

  const p = privacy.data;
  if (!p) return <p className="text-muted">{t('common.loading')}</p>;
  const logLabel = (action: string) => (LOGGED.includes(action) ? t(`privacy.log.${action}` as TKey) : action);

  return (
    <SettingsPage orgId={orgId} aside={<HelpAside slugs={['privacy']} />}>
      <PageHeader orgId={orgId} title={t('privacy.title')} intro={t('privacy.intro')} />
      {salon.data && !owner && <p className="text-sm text-muted">{t('privacy.ownerOnly')}</p>}

      <Section title={t('privacy.dpa')} intro={t('privacy.dpaIntro')}>
        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[15px]">
          {(['privacy.dpaPoint1', 'privacy.dpaPoint2', 'privacy.dpaPoint3', 'privacy.dpaPoint4'] as const).map((k) => (
            <li key={k}>{t(k)}</li>
          ))}
        </ul>
        <p className="flex flex-wrap gap-x-4 text-sm">
          <a href="/juridisch/verwerkersovereenkomst" target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            {t('legal.readDpa')} ↗
          </a>
          <a href="/juridisch/subverwerkers" target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            {t('legal.subprocessors')} ↗
          </a>
        </p>
        {p.dpa.upToDate && p.dpa.acceptedAt ? (
          <p className="text-sm font-medium text-accent-strong">✓ {t('privacy.dpaAccepted', { date: date(p.dpa.acceptedAt), version: p.dpa.currentVersion })}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-muted">{p.dpa.acceptedVersion ? t('privacy.dpaNew') : t('privacy.dpaNotYet')}</p>
            {owner && (
              <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
                {t('privacy.dpaAccept')}
              </Button>
            )}
          </div>
        )}
        <ErrorNote message={err(accept.error)} />
      </Section>

      <Section title={t('privacy.support')} intro={t('privacy.supportIntro')}>
        <p className="font-medium">{p.supportAccessUntil ? t('privacy.supportOn', { date: dateTime(p.supportAccessUntil) }) : t('privacy.supportOff')}</p>
        {p.supportSessions.map((s) => (
          <p key={s.until + s.name} className="rounded-[var(--radius-control)] bg-accent-soft px-3 py-2 text-sm text-accent-strong">
            {t('privacy.supportNow', { name: s.name, time: dateTime(s.until) })}
          </p>
        ))}
        {owner && (
          <div className="flex flex-wrap gap-2">
            {[1, 7, 30].map((d) => (
              <Button key={d} variant="secondary" onClick={() => support.mutate(d)} disabled={support.isPending}>
                {d === 1 ? t('privacy.supportDay') : t('privacy.supportFor', { n: d })}
              </Button>
            ))}
            {p.supportAccessUntil && (
              <Button variant="ghost" onClick={() => support.mutate(0)} disabled={support.isPending}>
                {t('privacy.supportStop')}
              </Button>
            )}
          </div>
        )}
        <ErrorNote message={err(support.error)} />
        {p.supportLog.length > 0 && (
          <div className="flex flex-col gap-1 border-t border-line pt-3">
            <p className="text-sm font-semibold">{t('privacy.supportLog')}</p>
            <ul className="flex flex-col gap-0.5 text-sm text-muted">
              {p.supportLog.map((l) => (
                <li key={l.at + l.action}>
                  {dateTime(l.at)}: {logLabel(l.action)}
                  {l.by ? ` (${l.by})` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section title={t('privacy.export')} intro={t('privacy.exportIntro')}>
        {owner && (
          <a
            href={`/api/v1/orgs/${orgId}/privacy/export`}
            className="inline-flex min-h-11 items-center self-start rounded-full border border-line bg-surface px-5 text-[15px] font-medium hover:bg-subtle"
            onClick={() => window.setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 1500)}
          >
            {t('privacy.exportButton')}
          </a>
        )}
        <p className="text-sm text-muted">{t('privacy.exportClients')}</p>
      </Section>
    </SettingsPage>
  );
}
